import _ from 'lodash';
import { inStock3300 } from '@/services/centerweightviewSiteNew';
import { crudRequest } from '@/services/api';

async function fetchFlowOrder(params) {
  try {
    const response = await crudRequest('/api/route/site/getRoadNumDisposition', params);

    const { data = [] } = response || {};
    return data;
  } catch (error) {
    console.error('Error fetching fullRateStandard:', error);
    return [];
  }
}

// 根据 smallArray 的 sectionOrder 对 largeArray 进行排序
function sortLargeArrayBySmallArray(largeArray, smallArray) {
  // 创建一个映射，生成一个用于排序的键
  const sortOrderMap = _.keyBy(
    smallArray,
    (item) => `${item?.departureSiteCode}-${item?.arriveSiteCode}`,
  );

  // 对 largeArray 进行排序
  return _.sortBy(largeArray, (item) => {
    const key = `${item?.scanCenterCode}-${item?.firstSiteCode}`;
    return sortOrderMap[key] ? sortOrderMap[key]?.sectionOrder : Infinity;
  });
}

/**
 * 处理 mergeData 数组，修改 levelOnSiteName 字段并按 levelOnSiteName 排序。
 * 截取 levelOnSiteName 中 '-' 分隔符之前的部分作为新的 levelOnSiteName。
 * @param {Array} data - 需要处理的数据数组
 * @returns {Array} 处理后的排序数据数组
 */
export function processAndSortData(data) {
  // 处理 levelOnSiteName 并排序
  return _.chain(data)
    .map((item) => ({
      ...item,
      levelOnSiteName: _.split(item.levelOnSiteName, '-')[0],
    }))
    .sortBy('levelOnSiteName')
    .value();
}

/**
 * 过滤并修改数据中心站点数据
 *
 * @param data 原始数据，默认为空数组
 * @returns 返回处理并排序后的数据
 */
export function filterChangeCenterSite(data = []) {
  const properties = [
    'originCalcWeight',
    'trainsCalcWeight',
    'unloadedCalcWeight',
    'superiorityWeight',
    'setTotal',
    'dailyCalcWeight',
    'loadedNotSent',
    'inStock3300CalcWeight',
  ];
  return processAndSortData(_.filter(data, (item) => _.some(properties, (prop) => item[prop])));
}

/**
 * 合并具有相同 levelOnSiteName 的数据记录。
 * 当 destinationSiteFlag 为 1 时，保留相同一级网点的多条记录。
 * 当 destinationSiteFlag 不为 1 时，使用原来的合并逻辑。
 * @param {Object} dataMap - 包含 key 和 value 数组的对象。
 * @param {Object} volumes - 体积相关数据。
 * @param {number} destinationSiteFlag - 标志是否组合 destinationSite。
 * @returns {Map} 合并后的数据对象。
 */
export function mergeSiteData(dataMap, volumes, destinationSiteFlag) {
  const mergedData = new Map();

  for (const [key, value] of dataMap) {
    if (destinationSiteFlag === 1) {
      const groupedByFirstSite = new Map();

      value.forEach((item) => {
        const firstSite = item.firstSite;
        if (!groupedByFirstSite.has(firstSite)) {
          groupedByFirstSite.set(firstSite, []);
        }
        groupedByFirstSite.get(firstSite).push(item);
      });

      for (const [firstSite, items] of groupedByFirstSite) {
        const mergedItem = createMergedItem(key, firstSite, items, volumes, true);
        mergedData.set(`${key}-${firstSite}`, mergedItem);
      }
    } else {
      // 原来的合并逻辑
      const mergedItem = createMergedItem(key, key, value, volumes, false);
      mergedData.set(key, mergedItem);
    }
  }

  return mergedData;
}
/**
 * 创建一个合并后的项目对象
 *
 * @param key 项目键值
 * @param firstSite 首个站点
 * @param items 项目列表
 * @param volumes 体积列表
 * @param includeDestination 是否包含目的地
 * @returns 合并后的项目对象
 */
function createMergedItem(key, firstSite, items, volumes, includeDestination) {
  const mergedItem = {
    key,
    scanCenter: '',
    firstSite,
    site: '',
    levelOnSiteName: key,
    sourceData: {},
    originCalcWeight: 0,
    trainsCalcWeight: 0,
    unloadedCalcWeight: 0,
    superiorityWeight: 0,
    setTotal: 0,
    dailyCalcWeight: 0,
    planTime: '',
    planDepartureTime: '',
    loadedNotSentCalcWeight: 0,
    inStock3300CalcWeight: 0,
    companyVolume: 0,
    coordinateToReceiveCenterDistance: '',
    firstSiteCode: '',
    scanCenterCode: '',
  };

  if (includeDestination) {
    mergedItem.destinationSite = '';
    mergedItem.destinationSiteCode = '';
  }

  items.forEach((item) => {
    // 更新共同字段
    mergedItem.scanCenter = item.scanCenter || mergedItem.scanCenter;
    mergedItem.site = item.site || mergedItem.site;
    mergedItem.firstSiteCode = item.firstSiteCode || mergedItem.firstSiteCode;
    mergedItem.scanCenterCode = item.scanCenterCode || mergedItem.scanCenterCode;

    if (includeDestination) {
      mergedItem.destinationSite = item.destinationSite || mergedItem.destinationSite;
      mergedItem.destinationSiteCode = item.destinationSiteCode || mergedItem.destinationSiteCode;
    }

    mergedItem.originCalcWeight += Number(item.originCalcWeight || 0);
    mergedItem.trainsCalcWeight += Number(item.trainsCalcWeight || 0);
    mergedItem.unloadedCalcWeight += Number(item.unloadedCalcWeight || 0);
    mergedItem.superiorityWeight += Number(item.superiorityWeight || 0);
    mergedItem.dailyCalcWeight += Number(item.dailyCalcWeight || 0);
    mergedItem.loadedNotSentCalcWeight += Number(item.loadedNotSentCalcWeight || 0);
    mergedItem.inStock3300CalcWeight += Number(item.inStock3300CalcWeight || 0);
    mergedItem.companyVolume = Number(item.companyVolume || 0);

    // // 查找并设置计划时间
    // const itemPlanTime = findPlanTime(item, volumes);
    // if (itemPlanTime && (!mergedItem.planTime || itemPlanTime < mergedItem.planTime)) {
    //   mergedItem.planTime = itemPlanTime;
    // }

    // 存储不同来源的特定数据
    mergedItem.sourceData[item.source] = {
      calcWeight: item.calcWeight,
      ...(item.originCalcWeight && { originCalcWeight: item.originCalcWeight }),
      ...(item.dailyCalcWeight && { dailyCalcWeight: item.dailyCalcWeight }),
      ...(item.loadedNotSentCalcWeight && {
        loadedNotSentCalcWeight: item.loadedNotSentCalcWeight,
      }),
      ...(item.inStock3300CalcWeight && { inStock3300CalcWeight: item.inStock3300CalcWeight }),
      ...(item.volumes && { volumes: item.volumes }),
      ...(item.period && { period: item.period }),
      ...(item.departureTime && { departureTime: item.departureTime }),
      ...(item.planTime && { planTime: item.planTime }),
      ...(item.departureCenter && { departureCenter: item.departureCenter }),
      ...(item.arrivalCenter && { arrivalCenter: item.arrivalCenter }),
    };
  });

  // 计算总重
  mergedItem.setTotal = Number.parseFloat(
    // mergedItem.originCalcWeight +
    mergedItem.trainsCalcWeight +
      mergedItem.unloadedCalcWeight +
      mergedItem.loadedNotSentCalcWeight,
    mergedItem.inStock3300CalcWeight,
  ).toFixed(1);

  // 将所有数值字段转换为字符串并保留一位小数
  [
    'originCalcWeight',
    'trainsCalcWeight',
    'unloadedCalcWeight',
    'superiorityWeight',
    'dailyCalcWeight',
    'loadedNotSentCalcWeight',
    'inStock3300CalcWeight',
    'companyVolume',
  ].forEach((field) => {
    mergedItem[field] = Number(mergedItem[field]).toFixed(1);
  });

  return mergedItem;
}

/**
 * 格式化重量
 *
 * @param weight 重量值，支持数字或字符串类型，字符串类型时支持逗号分隔的千分位表示
 * @param fixed 保留的小数位数，默认为2位
 * @returns 格式化后的重量值，返回字符串类型，当重量值为0时返回数字0
 */
export function formatWeight(weight, fixed = 2) {
  if (_.isNil(weight) || weight === '') return null;

  const parsed = _.toNumber(_.isString(weight) ? weight.replace(/,/g, '') : weight);

  if (_.isNaN(parsed)) {
    console.error(`Error formatting weight: ${weight}`);
    return null;
  }

  const rounded = _.round(parsed, fixed);
  // 使用 parseFloat 来去掉末尾的多余0
  const formatted = parseFloat(rounded.toFixed(fixed));

  return formatted === 0 ? 0 : formatted.toString();
}

/**
 * 查找计划时间
 *
 * @param item 待查找计划时间的对象
 * @param volumes 包含计划时间的对象数组
 * @returns 返回计划时间
 */
// export function findPlanTime(item, volumes) {
//   const siteName = item.levelOnSiteName ? item.levelOnSiteName : '';
//   return _.get(_.find(volumes, ['arrivalCenter', siteName]), 'departureTime') || '--';
// }

// todo: combineData 存在性能问题,待优化....

/**
 * 合并来自多个来源的数据并处理成统一格式。
 * @param {Object} data - 包含来自不同来源的数据数组的对象。
 * @param {Array} data.0 - 网点计划数据（可选，默认：空数组）。
 * @param {Array} data.1 - 干线计划数据（可选，默认：空数组）。
 * @param {Array} data.2 - 库存数据（可选，默认：空数组）。
 * @param {Array} data.3 - 出发货量分散数据（可选，默认：空数组）。
 * @param {number} destinationSiteFlag - 标志是否组合 destinationSite。
 * @returns {Array} 合并和处理后的数据。
 */
export function combineData(
  {
    0: sitePlan = [],
    1: trunkLinePlan = [],
    2: repertory = [],
    3: departWeightDisperse = [],
    4: loadedNotSent = [],
    5: inStock3300 = [],
    6: getSitePlanVolumes = [],
  },
  destinationSiteFlag,
) {
  // Helper function to handle destination site based on flag
  const handleDestinationSite = (item) => {
    if (destinationSiteFlag === 1) {
      return {
        destinationSite: item.destinationSite || '', // 目的网点
        destinationSiteCode: item.destinationSiteCode || '',
      };
    }
    return {};
  };

  // 合并四个接口的数据到一个数组中
  const allData = [
    ...sitePlan.map((item) => ({
      ...item,
      source: 'sitePlan',
      originCalcWeight: formatWeight(item.calcWeight || '0'),
      site: item?.scanCenter?.replace('分拨中心', '') || '', // 本站
      levelOnSiteName: item.firstSite || '', // 一级网点
      ...handleDestinationSite(item),
    })),
    ...trunkLinePlan.map((item) => ({
      ...item,
      source: 'trunkLinePlan',
      trainsCalcWeight: formatWeight(item.calcWeight || '0'),
      site: item?.scanCenter?.replace('分拨中心', '') || '', // 本站
      levelOnSiteName: item.firstSite || '', // 一级网点
      ...handleDestinationSite(item),
    })),
    ...repertory.map((item, index) => ({
      ...item,
      source: 'repertory',
      index,
      unloadedCalcWeight: formatWeight(item.repertoryCalcWeight || '0'),
      superiorityWeight: formatWeight(item.repertorySuperiorityProductCalcWeight || '0'),
      site: item?.scanCenter?.replace('分拨中心', '') || '', // 本站
      levelOnSiteName: item.firstSite || '', // 一级网点
      ...handleDestinationSite(item),
    })),
    ...departWeightDisperse.map((item) => ({
      ...item,
      source: 'departWeightDisperse',
      dailyCalcWeight: formatWeight(item.calcWeight || '0'),
      site: item?.scanCenter?.replace('分拨中心', '') || '', // 本站
      levelOnSiteName: item.firstSite || '', // 一级网点
      ...handleDestinationSite(item),
    })),
    ...loadedNotSent.map((item) => ({
      ...item,
      source: 'loadedNotSent',
      loadedNotSentCalcWeight: formatWeight(item.calcWeight || '0', 1),
      site: item?.scanCenter?.replace('分拨中心', '') || '', // 本站
      levelOnSiteName: item.firstSite || '', // 一级网点
      ...handleDestinationSite(item),
    })),
    ...inStock3300.map((item) => ({
      ...item,
      source: 'inStock3300',
      inStock3300CalcWeight: formatWeight(item.calcWeight || '0', 1),
      site: item?.scanCenter?.replace('分拨中心', '') || '', // 本站
      levelOnSiteName: item.firstSite || '', // 一级网点
      ...handleDestinationSite(item),
    })),
  ];

  return processAndSortData(allData);
}

/**
 * 将千克转换为吨，并处理嵌套对象或数组中的数据
 *
 * @param data 要转换的数据
 * @returns 转换后的数据
 */
export function convertKgToTon(data) {
  return _.transform(data, (result, value, key) => {
    if (key === 'companyVolume') {
      result[key] = value;
      return;
    }
    if (key === 'firstSiteCode' || key === 'scanCenterCode') {
      result[key] = value;
    } else if (_.isPlainObject(value)) {
      result[key] = convertKgToTon(value);
    } else if (_.isArray(value)) {
      result[key] = _.map(value, convertKgToTon);
    } else if (_.isString(value) || _.isNumber(value)) {
      const num = _.toNumber(value);
      if (!_.isNaN(num)) {
        result[key] = _.round(num / 1000, 2).toString();
      } else {
        result[key] = value;
      }
    } else {
      result[key] = value;
    }
  });
}

/**
 * 从tableItems中筛选出满足条件的项
 *
 * @param tableItems 表格项数组
 * @param checkFields 需要检查的字段数组
 * @returns 筛选出满足条件的项组成的数组
 */
export function filterTableItems(tableItems) {
  const checkFields = [
    'originCalcWeight',
    'trainsCalcWeight',
    'unloadedCalcWeight',
    'superiorityWeight',
    'setTotal',
    'loadedNotSentCalcWeight',
    'inStock3300',
    // 'dailyCalcWeight',
  ];
  return processAndSortData(
    _.filter(tableItems, (item) =>
      _.some(checkFields, (field) => _.toNumber(_.get(item, field, 0)) > 0),
    ),
  );
}

/**
 * 处理 mergeData 数组，修改 levelOnSiteName 字段并按 levelOnSiteName 排序。
 * 截取 levelOnSiteName 中 '-' 分隔符之前的部分作为新的 levelOnSiteName。
 * @param {Array} mergeData - 合并后的数据数组
 * @returns {Array} 处理后的排序数据数组
 */
export function processLevelOnSiteName(mergeData) {
  // 处理 levelOnSiteName
  const processedData = _.map(mergeData, (item) => ({
    ...item,
    levelOnSiteName: _.split(item.levelOnSiteName, '-')[0],
    dailyCalcWeight: formatWeight(item.dailyCalcWeight),
    loadedNotSentCalcWeight: formatWeight(item.loadedNotSentCalcWeight, 1),
  }));

  // 按 levelOnSiteName 排序
  return _.sortBy(processedData, 'levelOnSiteName');
}

/**
 * 处理数据并排序
 *
 * @param dataMap 数据映射
 * @param volumeItem 体积项
 * @param destinationSiteFlag 目标站点标志
 * @returns 排序后的数据数组
 */
export function processDataAndSort(dataMap, volumeItem, destinationSiteFlag) {
  const mergedDataMap = mergeSiteData(dataMap, volumeItem, destinationSiteFlag);

  // 将 Map 转换为数组，然后进行排序
  const sortedData = _.chain(Array.from(mergedDataMap.values()))
    .map((item) => ({
      ...item,
      levelOnSiteName: _.split(item.levelOnSiteName, '-')[0],
    }))
    .sortBy('levelOnSiteName')
    .value();

  return sortedData;
}

function attachCompanyVolume(tableData = [], getSitePlanVolumes = []) {
  const companyVolumeMap = _.reduce(
    getSitePlanVolumes,
    (result, item) => {
      const key = item?.firstSite || '';

      if (!key) {
        return result;
      }

      result[key] = {
        companyVolume: formatWeight(item?.companyVolume || '0'),
        planDepartureTime: item?.planDepartureTime || '',
      };
      return result;
    },
    {},
  );

  return tableData.map((item) => {
    const key = item?.firstSite || '';
    const companyVolumeItem = companyVolumeMap[key];

    if (_.isNil(companyVolumeItem)) {
      return item;
    }

    return {
      ...item,
      // 公司运力不参与前面的分组和过滤，只在最终排序结果上按接货网点口径兜底回填。
      companyVolume: companyVolumeItem.companyVolume,
      planDepartureTime: companyVolumeItem.planDepartureTime,
    };
  });
}

function attachSiteDistance(tableData = [], getSitePlanDistance = []) {
  const distanceNameMap = _.reduce(
    getSitePlanDistance,
    (result, item) => {
      const key = item?.levelOnSiteName || '';

      if (!key) {
        return result;
      }

      result[key] = item?.coordinateToReceiveCenterDistance ?? '';
      return result;
    },
    {},
  );

  return tableData.map((item) => {
    const distance = distanceNameMap[item?.firstSite || ''];

    if (_.isNil(distance) || distance === '') {
      return item;
    }

    return {
      ...item,
      // 分拨到网点距离不参与前面的分组和过滤，只在最终排序结果上按接货网点口径兜底回填。
      coordinateToReceiveCenterDistance: distance,
    };
  });
}

function attachCapacityGap(tableData = []) {
  return tableData.map((item) => {
    const companyVolume = Number(item?.companyVolume || 0);
    const setTotal = Number(item?.setTotal || 0);

    return {
      ...item,
      capacityGap: (companyVolume - setTotal).toFixed(1),
    };
  });
}


/**
 * 处理所有数据
 * 重要项：centerWeightViewOutletsTablesNew 中的 tableItem2 在此处理
 *
 * @param AllResult 包含网点计划数据、干线计划数据、库存数据和出发货量数据的数组
 * @param sitePlanParams 站点计划参数
 * @param volumeItem 体积项
 * @returns 处理后的最终数据
 */
export async function processAllData(AllResult, sitePlanParams, volumeItem) {
  try {
    const destinationSiteFlag = sitePlanParams.destinationSiteFlag;
    const dataFilterType = destinationSiteFlag !== 1 ? 'levelOnSiteName' : 'destinationSite';

    console.clear();
    console.log(new Date().toLocaleString());
    console.log(`是否展示目的网点：${destinationSiteFlag ? '是' : '否'}`);
    console.group('数据获取和处理过程');

    // 原始数据日志
    console.groupCollapsed('原始数据');
    console.log('getSitePlan 网点计划数据:', AllResult[0]);
    console.log('getTrunkLinePlan 干线计划数据:', AllResult[1]);
    console.log('getRepertory 库存数据:', AllResult[2]);
    console.log('departWeightDisperse 出发货量数据:', AllResult[3]);
    console.log('loadedNotSent 已装未发数据:', AllResult[4]);
    console.log('3300 在库3300数据:', AllResult[5]);
    console.log('getSitePlanVolumes 公司运力数据:', AllResult[6]);
    console.log('getSitePlanDistance 分拨到网点距离数据:', AllResult[7]);
    console.groupEnd();

    // 数据合并
    let combinedResult;
    try {
      combinedResult = combineData(AllResult, destinationSiteFlag);
      console.groupCollapsed('数据合并');
      console.log('combinedResult 上述四种数据合并-未去重:', combinedResult);
      console.groupEnd();
    } catch (error) {
      console.error('数据合并过程中发生错误:', error);
      throw error;
    }

    // 数据分组
    let groupedData, groupedData2;
    try {
      groupedData = _.groupBy(combinedResult, dataFilterType);
      groupedData2 = _.groupBy(
        combinedResult,
        (item) => `${item.levelOnSiteName}-${item.destinationSite}`,
      );
      console.groupCollapsed('数据分组');
      console.log(`groupedData 按照${dataFilterType}分组:`, groupedData);
      console.log(`groupedData2 按照 一级网点-目的网点 进行分组`, groupedData2);
      console.groupEnd();
    } catch (error) {
      console.error('数据分组过程中发生错误:', error);
      throw error;
    }

    // 数据序列化
    const dataMap = new Map(Object.entries(destinationSiteFlag ? groupedData2 : groupedData));
    console.groupCollapsed('数据序列化');
    console.log('dataMap 序列化数组:', dataMap);
    console.groupEnd();

    // 数据合并
    let mergeData;
    try {
      mergeData = Array.from(mergeSiteData(dataMap, volumeItem, destinationSiteFlag).values());
      console.groupCollapsed('数据合并');
      console.log(`mergeData 合并具有相同 ${dataFilterType} 的数据记录:`, mergeData);
      console.groupEnd();
    } catch (error) {
      console.error('数据合并过程中发生错误:', error);
      throw error;
    }

    // 数据过滤
    let filteredTableItem;
    try {
      filteredTableItem = filterTableItems(mergeData);
      console.groupCollapsed('数据过滤');
      console.log('filteredTableItems 过滤留个六个数组为0后的数据:', filteredTableItem);
      console.groupEnd();
    } catch (error) {
      console.error('数据过滤过程中发生错误:', error);
      throw error;
    }

    // 单位转换和处理一级站点名称
    let convertKgToTonData;
    try {
      convertKgToTonData = processLevelOnSiteName(convertKgToTon(filteredTableItem));
      console.groupCollapsed('单位转换');
      console.log('convertKgToTonData 将kg数据转为为吨:', convertKgToTonData);
      console.groupEnd();
    } catch (error) {
      console.error('单位转换和处理一级站点名称过程中发生错误:', error);
      throw error;
    }

    // 最终处理
    let finalData;
    try {
      finalData = processLevelOnSiteName(convertKgToTonData);
      console.groupCollapsed('截取一级站点名称');
      console.log('finalData 截取一级站点名称:', finalData);
      console.groupEnd();
    } catch (error) {
      console.error('最终处理过程中发生错误:', error);
      throw error;
    }

    console.groupEnd(); // 结束主组
    console.log(new Date().toLocaleString());

    // 根据流向配置进行排序
    const flowOrder = await fetchFlowOrder(sitePlanParams);
    const sortedArray = sortLargeArrayBySmallArray(finalData, flowOrder);
    //兜底 公司运力拼接
    const tableDataWithCompanyVolume = attachCompanyVolume(sortedArray, AllResult[6]);
    //兜底 公司距离拼接
    const tableDataWithDistance = attachSiteDistance(tableDataWithCompanyVolume, AllResult[7]);
    // 运力缺口 = 公司自有运力 - 总预计出发货量
    const finalSortedArray = attachCapacityGap(tableDataWithDistance);

    console.log(
      '%c [ sortedArray ]-250',
      'font-size:13px; background:pink; color:#bf2c9f;',
      finalSortedArray,
    );

    return finalSortedArray;
  } catch (error) {
    console.error('数据处理过程中发生错误:', error);
    throw error; // 或者根据需求处理错误
  }
}
